import React, { useState } from "react";
import { Check, Loader2, Plus, X } from "lucide-react";

interface AddGoalCardProps {
  /** Resolves true once the goal is saved, false to keep the form open. */
  onAdd: (goal: { title: string; description: string }) => Promise<boolean>;
}

/**
 * "Add your own goal", shown under the AI-suggested goals.
 *
 * Collapsed it is a dashed prompt; opened it is the same title + Result
 * editor a goal card shows when you edit it (GoalSelectionCard), so writing a
 * goal from scratch looks and behaves like correcting a suggested one.
 */
export const AddGoalCard: React.FC<AddGoalCardProps> = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [detail, setDetail] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setOpen(false);
    setTitle("");
    setDetail("");
  };

  const save = async () => {
    if (!title.trim() || saving) return;
    setSaving(true);
    const added = await onAdd({ title: title.trim(), description: detail.trim() });
    setSaving(false);
    if (added) reset();
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-secondary-200 dark:border-white/10 p-5 text-sm font-semibold text-accent-250 dark:text-pab-dark-subtle-text transition-colors hover:border-primary-350 hover:text-secondary-900 dark:hover:text-white"
      >
        <Plus size={16} />
        Add your own goal
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-xl border-2 border-primary-350 bg-secondary-50 dark:bg-white/5 p-6">
      <p className="text-xs font-semibold text-accent-250 dark:text-pab-dark-subtle-text">Goal</p>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void save();
            else if (e.key === "Escape") reset();
          }}
          maxLength={255}
          placeholder="e.g. Lead a cross-functional launch by Q3"
          aria-label="Goal title"
          className="flex-1 min-w-0 text-base text-secondary-900 dark:text-white bg-white dark:bg-white/5 border border-secondary-200 dark:border-white/10 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-350 focus:border-primary-350"
          autoFocus
        />
        <button
          type="button"
          onClick={() => void save()}
          disabled={!title.trim() || saving}
          className="flex-shrink-0 p-2 rounded-lg bg-ai-goal-category-bg text-ai-goal-category-text hover:opacity-80 transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
          title="Add goal"
          aria-label="Add goal"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
        </button>
        <button
          type="button"
          onClick={reset}
          disabled={saving}
          className="flex-shrink-0 p-2 rounded-lg text-accent-250 dark:text-pab-dark-subtle-text hover:text-secondary-900 dark:hover:text-white hover:bg-secondary-100 dark:hover:bg-white/10 transition-colors"
          title="Cancel"
          aria-label="Cancel"
        >
          <X size={16} />
        </button>
      </div>
      <div>
        <p className="mb-1 text-xs font-semibold text-accent-250 dark:text-pab-dark-subtle-text">
          Result <span className="font-normal">(optional)</span>
        </p>
        <textarea
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
          rows={2}
          placeholder="What does success look like?"
          aria-label="Goal result"
          className="w-full text-base text-secondary-900 dark:text-white bg-white dark:bg-white/5 border border-secondary-200 dark:border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-350 focus:border-primary-350 resize-none"
        />
      </div>
    </div>
  );
};

export default AddGoalCard;
